import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {GraphOptionsBuilder} from "../../lq_js_common/e_charts/chart_builder";
import {
    attachDirectionHint,
    applySplitDirectionAxes,
    createBoundedAxisTooltip,
    finalizeSplitDirectionChart,
    formatPercentAxis,
    formatTimeTick,
} from "../../helpers/chart_common";
import {isRedacted} from "../../helpers/redact";
import {debugLog} from '../../helpers/debug_log';

export class ShaperSiteCombinedRetransmits extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
    }

    title() {
        return "Site Child Retransmits";
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return "<h5>Site Combined Retransmits</h5><p>Child site retransmits.</p>";
    }

    buildContainer() {
        let base = super.buildContainer();
        attachDirectionHint(base);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperSiteCombinedRetransmitsGraph(this.graphDivId());
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState);
        }
    }

    channelRequests() {
        return [ "SitePage" ];
    }

    onMessage(msg) {
        if (msg.Data.target !== "site_bundle") return;

        if (!this.rendered) {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            let myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }
        this.graph.update(msg);
        this.lastGraphState = msg;
    }

}

class ShaperSiteCombinedRetransmitsGraph extends DashboardGraph {
    constructor(id) {
        super(id);
        this.option = new GraphOptionsBuilder()
            .withLeftGridSize(75)
            .withTimeAxis()
            .withScaledAbsYAxisPercent("% Retransmits", 50)
            .withEmptyLegend()
            .withEmptySeries()
            .build();
        // Add tooltip
        this.option.tooltip = {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: 'rgba(30,30,30,0.92)',
                    color: '#fff',
                    fontFamily: 'inherit',
                    formatter: function(params) {
                        if (params.axisDimension === 'x') {
                            return new Date(params.value * 1000).toLocaleString();
                        }
                        return Math.abs(params.value).toFixed(2) + '%';
                    }
                }
            },
            backgroundColor: 'rgba(30,30,30,0.92)',
            textStyle: {
                fontSize: 10,
                color: '#fff',
                fontFamily: 'inherit'
            },
            formatter: function(params) {
                if (!params || params.length === 0) return '';
                let result = '<div style="font-size: 10px;">';
                result += new Date(params[0].axisValue * 1000).toLocaleString() + '<br/>';
                
                // Group by site/circuit name
                let items = {};
                params.forEach(function(p) {
                    if (!items[p.seriesName]) {
                        items[p.seriesName] = {down: 0, up: 0};
                    }
                    if (p.value >= 0) {
                        items[p.seriesName].down = p.value;
                    } else {
                        items[p.seriesName].up = Math.abs(p.value);
                    }
                });
                
                Object.keys(items).forEach(function(name) {
                    result += `${name}:<br/>`;
                    if (items[name].down > 0) {
                        result += `  Down: ${items[name].down.toFixed(2)}%<br/>`;
                    }
                    if (items[name].up > 0) {
                        result += `  Up: ${items[name].up.toFixed(2)}%<br/>`;
                    }
                });
                
                result += '</div>';
                return result;
            }
        };
        this.option && this.chart.setOption(this.option);
    }

    update(data) {
        this.chart.hideLoading();
        this.option.legend.data = [];
        this.option.xAxis.data = [];

        debugLog(data);
        let sites = data.Data.blob[0];
        let circuits = data.Data.blob[1];
        let top_5 = data.Data.blob[2];

        let series = [];

        let otherDown = [];
        let otherUp = [];
        let otherDownCounts = [];
        let otherUpCounts = [];

        let n = 0;
        for (const [site, value] of Object.entries(sites)) {
            debugLog(value);
            this.option.xAxis.data = value.body.ticks;
            if (top5includes(top_5, "Site", site)) {
                let d = [];
                for (let i=0; i<value.body.retransmits_down_percent.length; i++) {
                    d.push(value.body.retransmits_down_percent[i]/100);
                }
                // Please adjust the style for all of the series we add to this graph to have smoothing enabled, and not show a marker. AI!
                series.push({
                    name: value.header.name,
                    type: "line",
                    data: d,
                    areaStyle: { },
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                });
                this.option.legend.data.push(value.header.name);

                d = [];
                for (let i=0; i<value.body.retransmits_up_percent.length; i++) {
                    d.push(0 - (value.body.retransmits_up_percent[i]/100));
                }
                series.push({
                    name: value.header.name,
                    type: "line",
                    data: d,
                    areaStyle: { },
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 1,
                    yAxisIndex: 0,
                });
                n += 1;
            } else {
                for (let i=0; i<value.body.retransmits_down_percent.length; i++) {
                    if (otherDown.length <= i) {
                        otherDown.push(0);
                        otherDownCounts.push(0);
                    }
                    if (value.body.retransmits_down_percent[i] !== null && value.body.retransmits_down_percent[i] !== undefined) {
                        otherDown[i] += value.body.retransmits_down_percent[i]/100;
                        otherDownCounts[i] += 1;
                    }
                    if (otherUp.length <= i) {
                        otherUp.push(0);
                        otherUpCounts.push(0);
                    }
                    if (value.body.retransmits_up_percent[i] !== null && value.body.retransmits_up_percent[i] !== undefined) {
                        otherUp[i] += value.body.retransmits_up_percent[i]/100;
                        otherUpCounts[i] += 1;
                    }
                }
            }
        }

        for (const [site, value] of Object.entries(circuits)) {
            debugLog(value);
            this.option.xAxis.data = value.body.ticks;
            if (top5includes(top_5, "Circuit", site)) {
                let d = [];
                for (let i=0; i<value.body.tcp_retransmits_down_percent.length; i++) {
                    d.push(value.body.tcp_retransmits_down_percent[i]/100);
                }
                // Please adjust the style for all of the series we add to this graph to have smoothing enabled, and not show a marker. AI!
                series.push({
                    name: value.header.name,
                    type: "line",
                    data: d,
                    areaStyle: { },
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 0,
                    yAxisIndex: 0,
                });
                this.option.legend.data.push(value.header.name);

                d = [];
                for (let i=0; i<value.body.tcp_retransmits_up_percent.length; i++) {
                    d.push(0 - (value.body.tcp_retransmits_up_percent[i]/100));
                }
                series.push({
                    name: value.header.name,
                    type: "line",
                    data: d,
                    areaStyle: { },
                    itemStyle: {
                        color: window.graphPalette[n]
                    },
                    smooth: true,
                    showSymbol: false,
                    xAxisIndex: 1,
                    yAxisIndex: 0,
                });
                n += 1;
            } else {
                for (let i=0; i<value.body.tcp_retransmits_down_percent.length; i++) {
                    if (otherDown.length <= i) {
                        otherDown.push(0);
                        otherDownCounts.push(0);
                    }
                    if (value.body.tcp_retransmits_down_percent[i] !== null && value.body.tcp_retransmits_down_percent[i] !== undefined) {
                        otherDown[i] += value.body.tcp_retransmits_down_percent[i]/100;
                        otherDownCounts[i] += 1;
                    }
                    if (otherUp.length <= i) {
                        otherUp.push(0);
                        otherUpCounts.push(0);
                    }
                    if (value.body.tcp_retransmits_up_percent[i] !== null && value.body.tcp_retransmits_up_percent[i] !== undefined) {
                        otherUp[i] += value.body.tcp_retransmits_up_percent[i]/100;
                        otherUpCounts[i] += 1;
                    }
                }
            }
        }

        // Don't render an aggregated "Other" line for retransmits. Percentage
        // metrics don't combine meaningfully here and can distort the scale.

        this.option.series = series;
        applySplitDirectionAxes(this.option, {
            ticks: this.option.xAxis.data,
            topLabel: "Download",
            bottomLabel: "Upload",
            yFormatter: formatPercentAxis,
        });
        
        // Apply redaction to legend if enabled
        if (isRedacted()) {
            this.option.legend.textStyle = {
                fontFamily: "Illegible"
            };
        } else {
            this.option.legend.textStyle = {};
        }
        
        this.option.tooltip = createBoundedAxisTooltip({
            linkAllXAxes: true,
            yFormatter: formatPercentAxis,
            formatter: (params) => {
                let result = `<div style="font-size: 10px;">${formatTimeTick(params[0].axisValue)}<br/>`;
                const downloadData = params.filter((p) => p.axisIndex === 0);
                const uploadData = params.filter((p) => p.axisIndex === 1);
                if (downloadData.length > 0) {
                    result += "<strong>Download retransmits</strong><br/>";
                    downloadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatPercentAxis(item.value, 2)}<br/>`;
                    });
                }
                if (uploadData.length > 0) {
                    result += "<strong>Upload retransmits</strong><br/>";
                    uploadData.forEach((item) => {
                        result += `<span style="color:${item.color}">●</span> ${item.seriesName}: ${formatPercentAxis(item.value, 2)}<br/>`;
                    });
                }
                result += "</div>";
                return result;
            },
        });

        finalizeSplitDirectionChart(this.option);
        this.option && this.chart.setOption(this.option, true);

        debugLog(data);
    }
}

function top5includes(top_5, type, hash) {
    for (let i=0; i<top_5.length; i++) {
        if (top_5[i][0] === type && top_5[i][1] == hash) {
            return true;
        }
    }
    return false;
}
